import {
  type ChangeEvent,
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Eye, Plus, SquarePen, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import CampaignTable from "src/components/reusable/CampaignTable/CampaignTable";
import ConfirmationModal from "src/components/reusable/ConfirmationModal";
import NoDataDisplay from "src/components/reusable/NoDataDisplay";
import StyledInput from "src/components/reusable/StyledInput";
import { TablePagePagination } from "src/components/common";
import { getNumberLocale } from "src/utils/numberFormat";
import type { CustomList } from "../../EngagementInterfaces";
import { AudienceTags } from "./AudienceColumns";
import AudienceAddRowForm from "./AudienceAddRowForm";
import {
  AUDIENCE_KIND_LABEL_KEYS,
  formatCreatedDate,
  getAudienceListKind,
  isPredictedList,
  parseCreatedAt,
} from "./audienceListUtils";
import {
  formatLastOrder,
  getCustomerName,
  readField,
  rowMatchesQuery,
  type AddRowValues,
  type ListRow,
} from "./audienceListRows";
import { useAudienceListRows } from "./useAudienceListRows";
import { useAudienceListImport } from "./useAudienceListImport";
import { CSV_EXTENSION } from "../CreateAudience/CreateList/createList.constants";
import "./AudienceListModal.scss";

const PAGE_SIZE = 8;
const MODAL_WIDTH = "min(968px, calc(100vw - 32px))";
const CUSTOMER_COLUMN_WIDTH = "34%";
const EMPTY_VALUE = "—";
const DISCARD_ICON = "/Icons/alert-icon.svg";

interface AudienceListModalProps {
  list: CustomList;
  onClose: () => void;
  onSaved: () => void;
  onImported: () => void;
  translateLabel: (_value: string | null | undefined) => string;
  formatMembers: (_list: CustomList) => string;
}

const AudienceListModal: FC<AudienceListModalProps> = ({
  list,
  onClose,
  onSaved,
  onImported,
  translateLabel,
  formatMembers,
}) => {
  const { t, i18n } = useTranslation();
  const label = useCallback((key: string) => t(`audienceOverview.listModal.${key}`), [t]);
  const editable = !isPredictedList(list);
  const kind = getAudienceListKind(list.list_type);
  const listName = translateLabel(list.list_name) || list.list_name;

  const { rows, columns, loading, saving, dirty, addRow, deleteRow, save, reload } =
    useAudienceListRows(list.custom_list_id);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddRow, setShowAddRow] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmImport, setConfirmImport] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImported = useCallback(() => {
    setShowAddRow(false);
    reload();
    onImported();
  }, [reload, onImported]);

  const { importing, importFiles } = useAudienceListImport(list.custom_list_id, handleImported);

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return query ? rows.filter(row => rowMatchesQuery(row.data, columns, query)) : rows;
  }, [rows, columns, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(page => Math.min(page, totalPages));
  }, [totalPages]);

  const createdLabel = useMemo(() => {
    const createdAt = parseCreatedAt(list.created_at);
    if (!createdAt) {
      return EMPTY_VALUE;
    }
    const time = new Intl.DateTimeFormat(getNumberLocale(i18n.language), {
      hour: "numeric",
      minute: "2-digit",
    }).format(createdAt.toDate());
    return `${formatCreatedDate(createdAt, i18n.language)} · ${time}`;
  }, [list.created_at, i18n.language]);

  const handleAddRow = useCallback(
    (values: AddRowValues) => {
      addRow(values);
      setShowAddRow(false);
    },
    [addRow],
  );

  const handleRequestClose = () => {
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const handleImportClick = () => {
    if (dirty) {
      setConfirmImport(true);
      return;
    }
    openFilePicker();
  };

  const handleConfirmImport = () => {
    setConfirmImport(false);
    openFilePicker();
  };

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    importFiles(files);
  };

  const handleSave = async () => {
    if (await save()) {
      onSaved();
    }
  };

  const tableColumns = useMemo<ColumnsType<ListRow>>(() => {
    const renderText = (field: "phone" | "totalOrders", className: string) => (_: unknown, row: ListRow) => (
      <span className={className}>{readField(row.data, columns, field) || EMPTY_VALUE}</span>
    );

    const result: ColumnsType<ListRow> = [
      {
        title: label("customer"),
        key: "customer",
        width: CUSTOMER_COLUMN_WIDTH,
        render: (_, row) => {
          const name = getCustomerName(row.data, columns);
          const email = readField(row.data, columns, "email");
          return (
            <div className="audience-list-modal__customer">
              <span className="audience-list-modal__customer-name" dir="auto">
                {name || email || EMPTY_VALUE}
              </span>
              {name && email && <span className="audience-list-modal__customer-email">{email}</span>}
            </div>
          );
        },
      },
    ];

    if (columns.phone) {
      result.push({
        title: label("phone"),
        key: "phone",
        render: renderText("phone", "audience-list-modal__muted"),
      });
    }
    if (columns.totalOrders) {
      result.push({
        title: label("totalOrders"),
        key: "totalOrders",
        render: renderText("totalOrders", "audience-list-modal__value"),
      });
    }
    if (columns.totalSpent) {
      result.push({
        title: label("totalSpent"),
        key: "totalSpent",
        render: (_, row) => (
          <span className="audience-list-modal__value audience-list-modal__value--strong">
            {readField(row.data, columns, "totalSpent") || EMPTY_VALUE}
          </span>
        ),
      });
    }
    if (columns.lastOrder) {
      result.push({
        title: label("lastOrder"),
        key: "lastOrder",
        render: (_, row) => (
          <span className="audience-list-modal__value">
            {formatLastOrder(readField(row.data, columns, "lastOrder")) || EMPTY_VALUE}
          </span>
        ),
      });
    }
    if (editable) {
      result.push({
        title: label("action"),
        key: "action",
        width: 80,
        render: (_, row) => (
          <button
            type="button"
            className="audience-list-modal__delete"
            aria-label={label("deleteRow")}
            onClick={() => deleteRow(row.id)}
          >
            <Trash2 size={16} aria-hidden />
          </button>
        ),
      });
    }
    return result;
  }, [columns, editable, label, deleteRow]);

  const tableFooter = (
    <>
      {editable &&
        (showAddRow ? (
          <AudienceAddRowForm
            listName={listName}
            columns={columns}
            onAdd={handleAddRow}
            onCancel={() => setShowAddRow(false)}
          />
        ) : (
          <div className="audience-list-modal__add-row-link-wrap">
            <button
              type="button"
              className="audience-list-modal__add-row-link"
              onClick={() => setShowAddRow(true)}
              disabled={loading}
            >
              <Plus size={14} aria-hidden />
              {label("addRowLink")}
            </button>
          </div>
        ))}
      <TablePagePagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPrevious={() => setCurrentPage(page => Math.max(1, page - 1))}
        onNext={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
      />
    </>
  );

  return (
    <>
      <Modal
        open
        centered
        width={MODAL_WIDTH}
        className="audience-list-modal"
        closeIcon={<X size={20} aria-label={label("closeAria")} />}
        onCancel={handleRequestClose}
        maskClosable={!dirty}
        footer={
          <div className="audience-list-modal__footer">
            <button
              type="button"
              className="audience-list-modal__secondary-btn"
              onClick={handleRequestClose}
            >
              {label("close")}
            </button>
            {editable && (
              <button
                type="button"
                className="button button--primary"
                onClick={handleSave}
                disabled={!dirty || saving || importing}
              >
                {saving ? label("saving") : label("saveChanges")}
              </button>
            )}
          </div>
        }
      >
        <div className="audience-list-modal__heading">
          <div className="audience-list-modal__title-row">
            <h2 className="audience-list-modal__title" dir="auto">{listName}</h2>
            <span className={`audience-type-badge audience-type-badge--${kind}`}>
              {t(AUDIENCE_KIND_LABEL_KEYS[kind])}
            </span>
          </div>
          <p className="audience-list-modal__subtitle" dir="auto">
            {translateLabel(list.description) ||
              list.description?.trim() ||
              (editable ? label("editableSubtitle") : label("predictedSubtitle"))}
          </p>
        </div>

        <div className="audience-list-modal__summary">
          <div className="audience-list-modal__summary-item">
            <span className="audience-list-modal__summary-label">{label("totalMembers")}</span>
            <strong className="audience-list-modal__members">{formatMembers(list)}</strong>
          </div>
          <div className="audience-list-modal__summary-item">
            <span className="audience-list-modal__summary-label">{label("created")}</span>
            <span className="audience-list-modal__summary-value">{createdLabel}</span>
          </div>
          <div className="audience-list-modal__summary-item">
            <span className="audience-list-modal__summary-label">{label("tags")}</span>
            {list.tags?.length ? (
              <AudienceTags tags={list.tags} translateLabel={tag => translateLabel(tag) || tag} />
            ) : (
              <span className="audience-list-modal__summary-value">{EMPTY_VALUE}</span>
            )}
          </div>
          <div className="audience-list-modal__access">
            <span className="audience-list-modal__access-label">
              {editable ? <SquarePen size={14} aria-hidden /> : <Eye size={14} aria-hidden />}
              {editable ? label("editable") : label("viewOnly")}
            </span>
            <span className="audience-list-modal__access-hint">
              {editable ? label("editableHint") : label("viewOnlyHint")}
            </span>
          </div>
        </div>

        <div className="audience-list-modal__toolbar">
          <StyledInput
            placeholder={label("searchPlaceholder")}
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            className="audience-list-modal__search"
          />
          {editable && (
            <>
              <button
                type="button"
                className="audience-list-modal__secondary-btn audience-list-modal__import"
                onClick={handleImportClick}
                disabled={loading || saving || importing}
              >
                {importing ? label("importing") : label("importList")}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={CSV_EXTENSION}
                multiple
                hidden
                onChange={handleFilesSelected}
              />
            </>
          )}
        </div>

        <div className="audience-list-modal__table-card">
          <CampaignTable<ListRow>
            className="audience-list-modal__table"
            columns={tableColumns}
            data={pageRows}
            rowKey="id"
            loading={loading}
            locale={{
              emptyText: (
                <NoDataDisplay
                  message={label("noMembers")}
                  altText={t("audienceOverview.noDataAlt")}
                />
              ),
            }}
          />
          {tableFooter}
        </div>
      </Modal>

      <ConfirmationModal
        modalOpen={confirmDiscard}
        title={label("discardTitle")}
        description={t("audienceOverview.listModal.discardDescription", { name: listName })}
        icon={DISCARD_ICON}
        confirmText={label("discard")}
        cancelText={label("keepEditing")}
        onConfirm={onClose}
        onCancel={() => setConfirmDiscard(false)}
      />

      <ConfirmationModal
        modalOpen={confirmImport}
        title={label("importDiscardTitle")}
        description={t("audienceOverview.listModal.importDiscardDescription", { name: listName })}
        icon={DISCARD_ICON}
        confirmText={label("importAnyway")}
        cancelText={label("keepEditing")}
        onConfirm={handleConfirmImport}
        onCancel={() => setConfirmImport(false)}
      />
    </>
  );
};

export default AudienceListModal;
